import React from 'react';
import {
  FaUserPlus,
  FaProjectDiagram,
  FaFileInvoiceDollar,
  FaHandshake,
  FaHardHat,
  FaCheckCircle,
} from 'react-icons/fa';
import { Project } from '../services/ProjectService';

interface HorizontalActivityTimelineProps {
  partnerCreatedAt?: string;
  projects?: Project[];
}

interface TimelineStep {
  key: string;
  label: string;
  icon: React.ReactNode;
  completed: boolean;
  date?: string;
  detail?: string;
}

export default function HorizontalActivityTimeline({
  partnerCreatedAt,
  projects = [],
}: HorizontalActivityTimelineProps): React.JSX.Element | null {
  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  // Determine which steps are completed based on data
  const hasContact = !!partnerCreatedAt;
  const hasProject = projects.length > 0;
  const latestProject = projects.length > 0
    ? projects.reduce((a, b) => new Date(a.created_at) > new Date(b.created_at) ? a : b)
    : null;

  // Check project statuses for further steps
  const hasOffer = projects.some(p =>
    p.status === 'OFFER_CREATED' || p.status === 'OFFER_SENT' ||
    p.status === 'ORDER_CONFIRMED' || p.status === 'IN_EXECUTION' || p.status === 'COMPLETED'
  );
  const hasOrder = projects.some(p =>
    p.status === 'ORDER_CONFIRMED' || p.status === 'IN_EXECUTION' || p.status === 'COMPLETED'
  );
  const hasExecution = projects.some(p =>
    p.status === 'IN_EXECUTION' || p.status === 'COMPLETED'
  );
  const hasCompleted = projects.some(p => p.status === 'COMPLETED');

  const steps: TimelineStep[] = [
    {
      key: 'contact',
      label: 'Kontakt erstellt',
      icon: <FaUserPlus size={16} />,
      completed: hasContact,
      date: partnerCreatedAt,
    },
    {
      key: 'project',
      label: 'Projekt angelegt',
      icon: <FaProjectDiagram size={16} />,
      completed: hasProject,
      date: latestProject?.created_at,
      detail: latestProject?.name,
    },
    {
      key: 'offer',
      label: 'Angebot erstellt',
      icon: <FaFileInvoiceDollar size={16} />,
      completed: hasOffer,
    },
    {
      key: 'order',
      label: 'Auftrag bestätigt',
      icon: <FaHandshake size={16} />,
      completed: hasOrder,
    },
    {
      key: 'execution',
      label: 'In Ausführung',
      icon: <FaHardHat size={16} />,
      completed: hasExecution,
    },
    {
      key: 'completed',
      label: 'Abgeschlossen',
      icon: <FaCheckCircle size={16} />,
      completed: hasCompleted,
    },
  ];

  // Find the last completed step index
  const lastCompletedIndex = steps.reduce((acc, step, idx) => step.completed ? idx : acc, -1);

  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #e0e3ef',
        borderRadius: '12px',
        padding: '20px 24px',
        marginBottom: '24px',
      }}
    >
      {/* Horizontal Stepper Timeline */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          position: 'relative',
        }}
      >
        {steps.map((step, index) => {
          const isCompleted = step.completed;
          const isLast = index === steps.length - 1;
          const lineCompleted = index < lastCompletedIndex;

          return (
            <div
              key={step.key}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                flex: 1,
                position: 'relative',
              }}
            >
              {/* Connecting line (before the dot) */}
              {index > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    top: '18px',
                    right: '50%',
                    width: '100%',
                    height: '2px',
                    background: lineCompleted ? '#021a4a' : '#e2e8f0',
                    zIndex: 0,
                  }}
                />
              )}

              {/* Icon Circle */}
              <div
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  background: isCompleted ? '#021a4a' : '#f1f5f9',
                  border: isCompleted ? '2px solid #021a4a' : '2px solid #e2e8f0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: isCompleted ? '#fff' : '#94a3b8',
                  position: 'relative',
                  zIndex: 1,
                  marginBottom: '8px',
                }}
              >
                {step.icon}
              </div>

              {/* Label */}
              <div
                style={{
                  fontSize: '12px',
                  fontWeight: 600,
                  color: isCompleted ? '#021a4a' : '#94a3b8',
                  textAlign: 'center',
                  marginBottom: '2px',
                }}
              >
                {step.label}
              </div>

              {/* Date & Detail */}
              {isCompleted && step.date && (
                <div
                  style={{
                    fontSize: '11px',
                    color: '#64748b',
                    textAlign: 'center',
                  }}
                >
                  {formatDate(step.date)}
                </div>
              )}
              {isCompleted && step.detail && (
                <div
                  style={{
                    fontSize: '10px',
                    color: '#94a3b8',
                    textAlign: 'center',
                    maxWidth: '100px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={step.detail}
                >
                  {step.detail}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
